import { EventProgramLinks } from '@/features/digitalPrograms/EventProgramLinks'
import { withErrorCode } from '@/lib/errors'
import { formatUserFacingError } from '@/lib/errors'
import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { AdminFormPage } from '@/features/admin/components/AdminFormPage'
import { adminEventsService } from '../services/admin.events.service'
import { TicketTypeFormRow } from '../components/TicketTypeFormRow'
import { useAuth } from '@/hooks/useAuth'
import type { ProductionDocument, TicketTypeDocument } from '@/types'
import type { EventFormValues, TicketTypeFormValues } from '../types'
import { adminProductionsService } from '@/features/admin/productions/services/adminProductions.service'
import { linkedEventDefaults } from '@/features/admin/productions/lib/linkedCreationDefaults'

// ── Schema ─────────────────────────────────────────────────────────────────────

const eventSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  slug: z
    .string()
    .min(1, 'Slug is required')
    .regex(/^[a-z0-9-]+$/, 'Slug must be lowercase letters, numbers, and hyphens only'),
  description: z.string().min(1, 'Description is required'),
  venue: z.string().min(1, 'Venue is required'),
  eventDate: z.string().min(1, 'Event date is required'),
  eventTime: z.string().min(1, 'Event time is required'),
  doorTime: z.string(),
  status: z.enum(['draft', 'published', 'cancelled']),
  isPublished: z.boolean(),
  order: z.number().int().min(0),
  capacity: z
    .string()
    .regex(/^[1-9]\d*$/, 'Show capacity must be a whole number greater than zero'),
  productionId: z.string(),
})

type EventValues = z.infer<typeof eventSchema>

function slugify(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim()
}

// ── Page ───────────────────────────────────────────────────────────────────────

export function AdminEventEditPage() {
  const { id } = useParams<{ id: string }>()
  const isNew = !id || id === 'new'
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user } = useAuth()
  const requestedProductionId = isNew ? (searchParams.get('productionId')?.trim() ?? '') : ''

  const [loading, setLoading] = useState(!isNew || Boolean(requestedProductionId))
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [linkedProduction, setLinkedProduction] = useState<
    (ProductionDocument & { id: string }) | null
  >(null)

  // Ticket types state (only relevant for existing events)
  const [ticketTypes, setTicketTypes] = useState<Array<TicketTypeDocument & { id: string }>>([])
  const [showNewTicketTypeRow, setShowNewTicketTypeRow] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<EventValues>({
    resolver: zodResolver(eventSchema),
    defaultValues: {
      title: '',
      slug: '',
      description: '',
      venue: '',
      eventDate: '',
      eventTime: '19:00',
      doorTime: '',
      status: 'draft',
      isPublished: false,
      order: 0,
      capacity: '',
      productionId: '',
    },
  })

  // Auto-generate slugs only for standalone events. Linked creation inherits
  // the production's canonical slug, which may intentionally differ from a
  // slug generated from its display name.
  const titleValue = watch('title')
  useEffect(() => {
    if (isNew && !requestedProductionId) {
      setValue('slug', slugify(titleValue))
    }
  }, [isNew, requestedProductionId, titleValue, setValue])

  // Load existing event
  useEffect(() => {
    if (isNew || !id) return
    setLoading(true)
    adminEventsService
      .getEventById(id)
      .then(async (event) => {
        if (!event) return
        const date = event.eventDate.toDate()
        reset({
          title: event.title,
          slug: event.slug,
          description: event.description,
          venue: event.venue,
          eventDate: date.toISOString().split('T')[0],
          eventTime: date.toTimeString().slice(0, 5),
          doorTime: event.doorTime ?? '',
          status: event.status,
          isPublished: event.isPublished,
          order: event.order,
          capacity: event.capacity ? String(event.capacity) : '',
          productionId: event.productionId ?? '',
        })
        if (event.productionId) {
          const production = await adminProductionsService.getById(event.productionId)
          if (production) setLinkedProduction({ ...production, id: event.productionId })
        }
      })
      .catch((err: unknown) => setSubmitError(formatUserFacingError(err, 'Failed to load event')))
      .finally(() => setLoading(false))

    // Load ticket types
    adminEventsService
      .getTicketTypes(id)
      .then(setTicketTypes)
      .catch(() => {
        /* non-critical */
      })
  }, [id, isNew, reset])

  // A production-scoped creation route inherits shared data once, before the
  // admin starts editing. Standalone /admin/events/new remains unchanged.
  useEffect(() => {
    if (!isNew || !requestedProductionId) return
    setLoading(true)
    setSubmitError(null)
    adminProductionsService
      .getById(requestedProductionId)
      .then((production) => {
        if (!production) {
          setSubmitError(
            withErrorCode(
              'The linked production could not be found. Return to the production and try again.',
              'app/validation'
            )
          )
          return
        }
        const linked = { ...production, id: requestedProductionId }
        const inherited = linkedEventDefaults(linked)
        setLinkedProduction(linked)
        reset({
          ...inherited,
          description: '',
          eventTime: '19:00',
          doorTime: '',
          status: 'draft',
          isPublished: false,
          order: 0,
          capacity: '',
        })
      })
      .catch((err: unknown) =>
        setSubmitError(formatUserFacingError(err, 'Failed to load linked production'))
      )
      .finally(() => setLoading(false))
  }, [isNew, requestedProductionId, reset])

  async function onSubmit(values: EventValues) {
    setSubmitError(null)
    try {
      const data: EventFormValues = {
        ...values,
        capacity: Number(values.capacity),
      }
      if (isNew) {
        const newId = await adminEventsService.createEvent(data, user?.uid ?? 'unknown')
        void navigate(`/admin/events/${newId}/edit`)
      } else if (id) {
        await adminEventsService.updateEvent(id, data)
        void navigate('/admin/events')
      }
    } catch (err: unknown) {
      setSubmitError(formatUserFacingError(err, 'Failed to save event'))
    }
  }

  // Ticket type handlers
  const refreshTicketTypes = useCallback(() => {
    if (!id || isNew) return
    adminEventsService.getTicketTypes(id).then(setTicketTypes)
  }, [id, isNew])

  async function handleSaveTicketType(typeId: string | undefined, values: TicketTypeFormValues) {
    if (!id) return
    if (typeId) {
      await adminEventsService.updateTicketType(id, typeId, values)
    } else {
      await adminEventsService.createTicketType(id, values)
    }
    refreshTicketTypes()
    setShowNewTicketTypeRow(false)
  }

  async function handleDeleteTicketType(typeId: string) {
    if (!id) return
    await adminEventsService.deleteTicketType(id, typeId)
    refreshTicketTypes()
  }

  return (
    <AdminFormPage
      title={isNew ? 'New Event' : 'Edit Event'}
      cardTitle={isNew ? 'Create a new event' : 'Update event details'}
      backTo={linkedProduction ? `/admin/productions/${linkedProduction.id}` : '/admin/events'}
      loading={loading}
      cardSkeletonClassName="h-96"
    >
      <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} className="space-y-5">
        <input type="hidden" {...register('productionId')} />
        {!isNew && (
          <EventProgramLinks eventId={id ?? ''} productionId={watch('productionId')} admin />
        )}
        {linkedProduction && (
          <div className="rounded-md border border-primary/20 bg-primary/5 p-3 text-sm">
            <p className="font-medium">Linked to {linkedProduction.name}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {requestedProductionId
                ? 'Title, slug, venue, and date were inherited from the production. This event starts as an unpublished draft with public sales closed; add the event-specific description, performance time, and ticket types before opening sales.'
                : 'This event belongs to the production. Participant and public ticket sales continue to use the production setup.'}
            </p>
            <Button asChild type="button" variant="link" size="sm" className="h-auto px-0 pt-2">
              <Link to={`/admin/productions/${linkedProduction.id}`}>View production</Link>
            </Button>
          </div>
        )}
        {requestedProductionId ? (
          <details className="rounded-md border bg-muted/20 px-3 py-2">
            <summary className="cursor-pointer text-sm font-medium">
              Customize inherited title and URL
            </summary>
            <div className="mt-3 space-y-4">
              <div className="space-y-1">
                <Label htmlFor="title">Title</Label>
                <Input id="title" placeholder="Summer Showcase 2025" {...register('title')} />
                {errors.title && (
                  <p className="text-sm text-destructive">
                    {withErrorCode(String(errors.title.message), 'app/validation')}
                  </p>
                )}
              </div>
              <div className="space-y-1">
                <Label htmlFor="slug">Slug</Label>
                <Input id="slug" placeholder="summer-showcase-2025" {...register('slug')} />
                {errors.slug && (
                  <p className="text-sm text-destructive">
                    {withErrorCode(String(errors.slug.message), 'app/validation')}
                  </p>
                )}
              </div>
            </div>
          </details>
        ) : (
          <>
            <div className="space-y-1">
              <Label htmlFor="title">Title</Label>
              <Input id="title" placeholder="Summer Showcase 2025" {...register('title')} />
              {errors.title && (
                <p className="text-sm text-destructive">
                  {withErrorCode(String(errors.title.message), 'app/validation')}
                </p>
              )}
            </div>

            <div className="space-y-1">
              <Label htmlFor="slug">Slug</Label>
              <Input id="slug" placeholder="summer-showcase-2025" {...register('slug')} />
              {errors.slug && (
                <p className="text-sm text-destructive">
                  {withErrorCode(String(errors.slug.message), 'app/validation')}
                </p>
              )}
            </div>
          </>
        )}

        <div className="space-y-1">
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            rows={4}
            placeholder="Tell attendees about this event…"
            {...register('description')}
          />
          {errors.description && (
            <p className="text-sm text-destructive">
              {withErrorCode(String(errors.description.message), 'app/validation')}
            </p>
          )}
        </div>

        <div className="space-y-1">
          <Label htmlFor="venue">Venue</Label>
          <Input id="venue" placeholder="123 Main St, City, State" {...register('venue')} />
          {errors.venue && (
            <p className="text-sm text-destructive">
              {withErrorCode(String(errors.venue.message), 'app/validation')}
            </p>
          )}
        </div>

        <div className="space-y-1">
          <Label htmlFor="capacity">Show Capacity</Label>
          <Input
            id="capacity"
            type="number"
            min={1}
            step={1}
            placeholder="150"
            {...register('capacity')}
          />
          <p className="text-xs text-muted-foreground">
            Total physical seats for this performance. Every public ticket, participant ticket, and
            checkout hold draws from this same pool.
          </p>
          {errors.capacity && (
            <p className="text-sm text-destructive">
              {withErrorCode(String(errors.capacity.message), 'app/validation')}
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label htmlFor="eventDate">Event Date</Label>
            <Input id="eventDate" type="date" {...register('eventDate')} />
            {errors.eventDate && (
              <p className="text-sm text-destructive">
                {withErrorCode(String(errors.eventDate.message), 'app/validation')}
              </p>
            )}
          </div>
          <div className="space-y-1">
            <Label htmlFor="eventTime">Event Time</Label>
            <Input id="eventTime" type="time" {...register('eventTime')} />
            {errors.eventTime && (
              <p className="text-sm text-destructive">
                {withErrorCode(String(errors.eventTime.message), 'app/validation')}
              </p>
            )}
          </div>
        </div>

        <div className="space-y-1">
          <Label htmlFor="doorTime">Door Time (optional)</Label>
          <Input id="doorTime" placeholder="Doors at 7:00 PM" {...register('doorTime')} />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label htmlFor="status">Status</Label>
            <select
              id="status"
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              {...register('status')}
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="cancelled">Cancelled</option>
            </select>
            {errors.status && (
              <p className="text-sm text-destructive">
                {withErrorCode(String(errors.status.message), 'app/validation')}
              </p>
            )}
          </div>
          <div className="space-y-1">
            <Label htmlFor="order">Sort Order</Label>
            <Input
              id="order"
              type="number"
              min={0}
              {...register('order', { valueAsNumber: true })}
            />
            {errors.order && (
              <p className="text-sm text-destructive">
                {withErrorCode(String(errors.order.message), 'app/validation')}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <input
            id="isPublished"
            type="checkbox"
            className="h-4 w-4 accent-primary"
            {...register('isPublished')}
          />
          <Label htmlFor="isPublished">Published (visible on public site)</Label>
        </div>

        {submitError && <p className="text-sm text-destructive">{submitError}</p>}

        <div className="flex gap-3 pt-2">
          <Button
            type="submit"
            disabled={isSubmitting || Boolean(requestedProductionId && !linkedProduction)}
          >
            {isSubmitting ? 'Saving…' : isNew ? 'Create Event' : 'Save Changes'}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() =>
              void navigate(
                linkedProduction ? `/admin/productions/${linkedProduction.id}` : '/admin/events'
              )
            }
          >
            Cancel
          </Button>
        </div>
      </form>

      {/* Ticket Types — only shown after event is created */}
      {!isNew && id && (
        <div className="mt-8 border-t pt-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">Ticket Types</h2>
              <p className="text-xs text-muted-foreground">
                {ticketTypes.reduce(
                  (total, ticketType) => total + ticketType.sold + (ticketType.reserved ?? 0),
                  0
                )}{' '}
                of {watch('capacity') || '—'} shared seats sold or held
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowNewTicketTypeRow(true)}
            >
              <Plus className="h-4 w-4 mr-1.5" />
              Add Ticket Type
            </Button>
          </div>

          {ticketTypes.length === 0 && !showNewTicketTypeRow && (
            <p className="text-sm text-muted-foreground">
              No ticket types yet. Add one to enable ticket sales.
            </p>
          )}

          <div className="space-y-3">
            {ticketTypes.map((tt) => (
              <TicketTypeFormRow
                key={tt.id}
                initialValues={{
                  id: tt.id,
                  name: tt.name,
                  description: tt.description ?? '',
                  price: tt.price / 100,
                  capacity: tt.capacity,
                  saleStartsAt: tt.saleStartsAt.toDate().toISOString().slice(0, 16),
                  saleEndsAt: tt.saleEndsAt.toDate().toISOString().slice(0, 16),
                  isVisible: tt.isVisible,
                  order: tt.order,
                }}
                onSave={(values) => handleSaveTicketType(tt.id, values)}
                onDelete={() => handleDeleteTicketType(tt.id)}
              />
            ))}

            {showNewTicketTypeRow && (
              <TicketTypeFormRow
                isNew
                defaultCapacity={Number(watch('capacity')) || undefined}
                onSave={(values) => handleSaveTicketType(undefined, values)}
                onCancel={() => setShowNewTicketTypeRow(false)}
              />
            )}
          </div>
        </div>
      )}
    </AdminFormPage>
  )
}
